import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { investorService, JourneyListItem } from '../services/investor.service';
import { setActiveInvestorJourney } from '../services/investorJourneyScope';
import AlertDialog from '../components/AlertDialog/AlertDialog';
import { SowAgreementModal } from '../views/Investor/MyConsents/SowAgreementModal';
import { KycReuseModal } from '../components/KycReuseModal/KycReuseModal';
import { kycDocumentsService, KycRequirementSlotDto } from '../services/kycDocuments.service';
import { sowService } from '../services/sow.service';

// Smart Upload benefit nudge is shown at most once per browser session (decision #1).

interface UseJourneyKycGate {
  openJourney: (item: JourneyListItem) => void;
  modals: React.ReactNode;
}

/**
 * Shared journey-open gate (KYC_OPTIONAL_SMART_UPLOAD_PLAN.md, Parts B1 + C).
 *
 * Owns the three prompts that gate opening a journey — the Smart Upload benefit nudge
 * (KYC not done), the per-journey KYC-reuse consent, and the "which address?" reconcile
 * prompt — so the investor dashboard and the journey list stay in sync from one place.
 *
 * The caller supplies {@link navigateToJourney} (the destination route differs per page)
 * and renders the returned {@link UseJourneyKycGate.modals}.
 */
export function useJourneyKycGate(
  navigateToJourney: (item: JourneyListItem) => void,
  logTag = 'JourneyKycGate'
): UseJourneyKycGate {
  const navigate = useNavigate();
  const [consentItem, setConsentItem] = useState<JourneyListItem | null>(null);
  // R1: the journey person's vault slots, shown in the reuse popup.
  const [consentSlots, setConsentSlots] = useState<KycRequirementSlotDto[]>([]);
  const [consentBusy, setConsentBusy] = useState(false);
  const [addressReconcile, setAddressReconcile] = useState(false);
  // Journey the investor was opening when the SOW prompt appeared, resumed after they agree.
  const [sowItem, setSowItem] = useState<JourneyListItem | null>(null);
  const [sowBusy, setSowBusy] = useState(false);
  // Tracks "I Agree" so the consent onClose doesn't also record a Skip.
  const agreedRef = useRef(false);

  // Gate the first open of a journey. KYC incomplete -> benefit nudge (once/session);
  // complete but not consented -> consent prompt; otherwise open straight through.
  const openJourney = async (item: JourneyListItem) => {
    if (!item.journeyId) {
      navigateToJourney(item);
      return;
    }
    try {
      const gate = await investorService.getJourneyKycGate(item.journeyId);
      // SOW gate comes FIRST: the Statement of Work is the foundational per-investor
      // agreement. Until it's agreed, route to the Consent Centre and don't run the KYC
      // prompts. (The journey page also enforces this as a backstop.)
      if (!gate.sowAgreed) {
        // Ask for the Statement of Work HERE rather than redirecting to the Consent Centre.
        // Bouncing the investor to another screen lost the journey they were opening and gave
        // no sense of why — they had to find their way back afterwards. Agreeing in place lets
        // the original click carry on.
        setSowItem(item);
        return;
      }
      if (!gate.kycComplete) {
        // UX F-10/F-11 (2026-08-22): first-time entry goes STRAIGHT into Smart Upload with
        // the journey context — no popup. A server-recorded skip (any of the page's three
        // exits) means resume INTO the journey on every later entry, across logins.
        if (gate.smartUploadSkipped) {
          navigateToJourney(item);
        } else {
          // C8 (audit 2026-08-25): only SET, never clear (null journeyId must not wipe the scope).
          if (item.journeyId) {
            setActiveInvestorJourney(item.journeyId);
          }
          navigate(`/investor/documents-center?journeyId=${encodeURIComponent(item.journeyId)}&from=journey`);
        }
        return;
      }
      if (gate.consentGiven) {
        navigateToJourney(item);
        return;
      }
      // R1: load the journey person's slot states so the popup can list what's
      // available vs still needed. Best-effort — an empty list still shows the popup.
      try {
        const req = item.journeyId ? await kycDocumentsService.getRequirements(item.journeyId) : null;
        setConsentSlots(req?.slots || []);
      } catch {
        setConsentSlots([]);
      }
      setConsentItem(item);
    } catch (error) {
      // Gate failure shouldn't trap the user — fall back to opening the journey.
      console.error(`[${logTag}] KYC gate check failed:`, error);
      navigateToJourney(item);
    }
  };

  // Agreeing to the SOW resumes the journey the investor originally clicked, by re-running the
  // gate — so the KYC prompts that would normally follow still run, rather than being skipped.
  const handleSowAgree = async () => {
    const item = sowItem;
    setSowBusy(true);
    try {
      await sowService.agreeMe('JOURNEY_KYC', item?.journeyId);
      setSowItem(null);
      if (item) await openJourney(item);
    } catch (error) {
      console.error(`[${logTag}] Failed to record SOW agreement:`, error);
      // Leave the modal open so the investor can retry rather than silently dropping them.
    } finally {
      setSowBusy(false);
    }
  };

  // Reconcile prompt: apply the latest document's address, or keep the existing one.
  const handleAddressUseLatest = () => {
    investorService
      .applyLatestKycAddress()
      .catch((error) => console.error(`[${logTag}] Failed to apply latest address:`, error));
    setAddressReconcile(false);
  };
  const handleAddressKeepExisting = () => setAddressReconcile(false);

  // Close the consent prompt and open the journey regardless of the choice — consent is
  // optional and never blocks entry. On Skip/X (no agree), record the decision (asked once).
  const closeConsentAndOpen = () => {
    const item = consentItem;
    if (item?.journeyId && !agreedRef.current) {
      investorService
        .skipJourneyKycConsent(item.journeyId)
        .catch((error) => console.error(`[${logTag}] Failed to record KYC skip:`, error));
    }
    agreedRef.current = false;
    setConsentItem(null);
    if (item) navigateToJourney(item);
  };

  // R1 "Reuse & continue": records consent + fills the profile + archives to THIS journey's
  // SharePoint/Dataverse (the reuse machinery IS the consent flow), then opens the journey.
  // If the documents disagree on address, surface the reconcile prompt.
  const handleConsentAgree = async () => {
    agreedRef.current = true; // set before onClose runs so it won't also record a Skip
    const item = consentItem;
    if (item?.journeyId) {
      setConsentBusy(true);
      try {
        const result = await investorService.giveJourneyKycConsent(item.journeyId);
        if (result?.addressConflict) setAddressReconcile(true);
      } catch (error) {
        console.error(`[${logTag}] Failed to record KYC consent:`, error);
      } finally {
        setConsentBusy(false);
      }
    }
    setConsentItem(null);
    agreedRef.current = false;
    if (item) navigateToJourney(item);
  };

  // R1 "Upload all fresh": into Smart Upload's fresh mode — every slot re-uploaded via the
  // supersede chain; the apply step runs on completion there (R2).
  const handleConsentFresh = () => {
    const item = consentItem;
    agreedRef.current = true; // deliberate choice — closing must not record a Skip
    setConsentItem(null);
    agreedRef.current = false;
    if (item?.journeyId) {
      setActiveInvestorJourney(item.journeyId);
      navigate(`/investor/documents-center?journeyId=${encodeURIComponent(item.journeyId)}&from=journey&fresh=1`);
    }
  };

  const modals = (
    <>
      <SowAgreementModal
        show={!!sowItem}
        busy={sowBusy}
        onClose={() => setSowItem(null)}
        onAgree={handleSowAgree}
      />
      <KycReuseModal
        show={!!consentItem}
        productName={consentItem?.product}
        slots={consentSlots}
        busy={consentBusy}
        onReuse={handleConsentAgree}
        onFresh={handleConsentFresh}
        onClose={closeConsentAndOpen}
      />
      <AlertDialog
        show={addressReconcile}
        title="Which address should we use?"
        message={`Your documents show more than one address. Use the address from your most recently uploaded document for this application?`}
        confirmText="Use the latest"
        cancelText="Keep existing"
        onConfirm={handleAddressUseLatest}
        onClose={handleAddressKeepExisting}
      />
    </>
  );

  return { openJourney, modals };
}
